import * as actionTypes from '../actions';

import {
    addItemInListOfItems, deleteItemByIdInListOfItems, emptyTablesForPending, getEmptyCurrentReservation, removeDuplicatedItems, updateItemInListOfItems
} from "../../helpers";
import { updateCacheDetailCustomer } from '@/queries/customers/utils';
import { invalidateReservationQueries } from '@/queries/reservations/utils';


const initialState = {
    reservations: [],
    currentReservation: getEmptyCurrentReservation(),
    standbyReservation: null,
    reservationsCounts: {},

    updateReservationsListLoading: false,

    getReservationsLoading: false,
    getReservationsError: null,

    addReservationLoading: false,
    addReservationError: null,

    deleteReservationLoading: false,
    deleteReservationError: null,

    updateReservationLoading: false,
    updateReservationError: null,

    confirmReservationLoading: false,
    confirmReservationError: null,

    cancelReservationLoading: false,
    cancelReservationError: null,

    markAsReadReservationLoading: false,
    markAsReadReservationError: null,

    reservationPaymentLoading: false,
    reservationPaymentError: null,
};

const RESERVATION_WRITE_ACTIONS = new Set([
    actionTypes.ADD_RESERVATION_SUCCESS,
    actionTypes.UPDATE_RESERVATION_SUCCESS,
    actionTypes.DELETE_RESERVATION_SUCCESS,
]);

const reservationsReducer = (state = initialState, action) => {

    if(RESERVATION_WRITE_ACTIONS.has(action.type)) {
        invalidateReservationQueries();
    }

    switch (action.type) {
        case actionTypes.SET_STANDBY_RESERVATION:
            return {
                ...state,
                standbyReservation: action.data,
            }

        // SET CURRENT RESERVATION
        case actionTypes.SET_CURRENT_RESERVATION:
            // const hasReservation = state.reservations.find(r => r.id === action.reservation.id);
            return {
                ...state,
                currentReservation: action.reservation,
            };

        // GET RESERVATIONS
        case actionTypes.GET_RESERVATIONS_REQUEST:
            return {
                ...state,
                getReservationsLoading: action.loading
            };
        case actionTypes.GET_RESERVATIONS_SUCCESS:
            const reservations = removeDuplicatedItems([
                ...action.reservations,
                ...state.reservations,
            ]);
            // Updating query cache from store.
            // Just legacy adapter
            for (const reservation of reservations) {
                if (reservation.customer)
                    updateCacheDetailCustomer(reservation.customer);
            }

            return {
                ...state,
                getReservationsLoading: action.loading,
                getReservationsError: action.error,
                // updating list with cached reservations in order to keep already fetched
                // older ones. If there are duplicates, new reservations have priority
                // over cached ones.
                reservations,
            };
        case actionTypes.GET_RESERVATIONS_ERROR:
            return {
                ...state,
                getReservationsLoading: action.loading,
                getReservationsError: action.error
            };

        // ADD RESERVATION
        case actionTypes.ADD_RESERVATION_REQUEST:
            return {
                ...state,
                addReservationLoading: action.loading
            };
        case actionTypes.ADD_RESERVATION_SUCCESS:
            return {
                ...state,
                addReservationLoading: action.loading,
                addReservationError: action.error,
                reservations: addItemInListOfItems(state.reservations, action.reservation)
            };
        case actionTypes.ADD_RESERVATION_ERROR:
            return {
                ...state,
                addReservationLoading: action.loading,
                addReservationError: action.error
            };

        // DELETE RESERVATION
        case actionTypes.DELETE_RESERVATION_REQUEST:
            return {
                ...state,
                deleteReservationLoading: action.loading
            };
        case actionTypes.DELETE_RESERVATION_SUCCESS:
            return {
                ...state,
                deleteReservationLoading: action.loading,
                deleteReservationError: action.error,
                reservations: deleteItemByIdInListOfItems(state.reservations, action.reservationId)
            };
        case actionTypes.DELETE_RESERVATION_ERROR:
            return {
                ...state,
                deleteReservationLoading: action.loading,
                deleteReservationError: action.error
            };

        // UPDATE RESERVATION
        case actionTypes.UPDATE_RESERVATION_REQUEST:
            return {
                ...state,
                updateReservationLoading: action.loading
            };
        case actionTypes.UPDATE_RESERVATION_SUCCESS:
            return {
                ...state,
                updateReservationLoading: action.loading,
                updateReservationError: action.error,
                reservations: updateItemInListOfItems(state.reservations, action.reservation).filter(r => !r.hidden)
            };
        case actionTypes.UPDATE_RESERVATION_ERROR:
            return {
                ...state,
                updateReservationLoading: action.loading,
                updateReservationError: action.error
            };

        case actionTypes.UPDATE_RESERVATION_LOCALLY:
            const currentReservation = (state.currentReservation?.id === action.reservation.id) ?
                { ...action.reservation } : state.currentReservation;

            return {
                ...state,
                currentReservation,
                reservations: updateItemInListOfItems(state.reservations, action.reservation),
            }

        // CONFIRM RESERVATION
        case actionTypes.CONFIRM_RESERVATION_REQUEST:
            return {
                ...state,
                confirmReservationLoading: action.loading
            };
        case actionTypes.CONFIRM_RESERVATION_SUCCESS:
            return {
                ...state,
                confirmReservationLoading: action.loading,
                confirmReservationError: action.error,
                reservations: updateItemInListOfItems(state.reservations, action.reservation)
            };
        case actionTypes.CONFIRM_RESERVATION_ERROR:
            return {
                ...state,
                confirmReservationLoading: action.loading,
                confirmReservationError: action.error
            };

        // CANCEL RESERVATION
        case actionTypes.CANCEL_RESERVATION_REQUEST:
            return {
                ...state,
                cancelReservationLoading: action.loading
            };
        case actionTypes.CANCEL_RESERVATION_SUCCESS:
            return {
                ...state,
                cancelReservationLoading: action.loading,
                cancelReservationError: action.error,
                reservations: updateItemInListOfItems(state.reservations, action.reservation)
            };
        case actionTypes.CANCEL_RESERVATION_ERROR:
            return {
                ...state,
                cancelReservationLoading: action.loading,
                cancelReservationError: action.error
            };

        // MARK AS READ RESERVATION
        case actionTypes.MARK_AS_READ_RESERVATION_REQUEST:
            return {
                ...state,
                markAsReadReservationLoading: action.loading
            };
        case actionTypes.MARK_AS_READ_RESERVATION_SUCCESS:
            return {
                ...state,
                markAsReadReservationLoading: action.loading,
                markAsReadReservationError: action.error,
                reservations: updateItemInListOfItems(state.reservations, action.reservation)
            };
        case actionTypes.MARK_AS_READ_RESERVATION_ERROR:
            return {
                ...state,
                markAsReadReservationLoading: action.loading,
                markAsReadReservationError: action.error
            };

        case actionTypes.SET_RESERVATIONS_LIST_LOADING:
            return {
                ...state,
                updateReservationsListLoading: !!action.loading,
            };

        case actionTypes.UPDATE_RESERVATION_COUNT:
            return {
                ...state,
                reservationsCounts: {
                    ...state.reservationsCounts,
                    [`${action.month}-${action.year}`]: action.reservationsCounts,
                },
            };

        case actionTypes.GET_RESERVATION_PAYMENT_REQUEST:
            return {
                ...state,
                reservationPaymentLoading: true,
                reservationPaymentError: null,
            };

        case actionTypes.GET_RESERVATION_PAYMENT_SUCCESS:

            const { payment } = action;

            return {
                ...state,
                // injecting updated payment in its reservation
                reservations: state.reservations
                    .map(reservation => (payment && reservation.payment?.id === payment.id) ? { ...reservation, payment, } : reservation),

                // injecting updated payment in current reservation if the payment belongs to it
                currentReservation: (payment && state.currentReservation.payment?.id === payment?.id) ?
                    { ...state.currentReservation, payment, } : state.currentReservation,

                reservationPaymentLoading: false,
                reservationPaymentError: null,
            };

        case actionTypes.GET_RESERVATION_PAYMENT_ERROR:
            return {
                ...state,
                reservationPaymentLoading: false,
                reservationPaymentError: action.error,
            };

        default:
            return state;
    }
};

export default reservationsReducer;


// WEBPACK FOOTER //
// ./src/store/reducers/reservations.js